import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CheckCircle, Circle } from "@phosphor-icons/react";
import { toast } from "sonner";

const CH = { instagram: "Instagram", facebook: "Facebook", blog: "Blog (lettori 7 gg)", tiktok: "TikTok", youtube: "YouTube", tutti: "Tutti i canali" };
const KEYS = ["facebook", "instagram", "tiktok", "youtube", "blog"];

function YouTubeId({ d, onSaved }) {
  const [v, setV] = useState(d.youtube_channel_id || "");
  const save = async () => { try { await api.put("/growth/settings", { youtube_channel_id: v }); toast.success("Canale YouTube salvato"); onSaved(); } catch (e) { toast.error(e.response?.data?.detail || "Errore"); } };
  return (
    <div className="flex flex-wrap gap-2 items-center text-sm" data-testid="growth-yt">
      <Input className="w-72" placeholder="ID canale YouTube (inizia con UC…)" value={v} onChange={(e) => setV(e.target.value)} data-testid="growth-yt-input" />
      <Button variant="outline" className="rounded-full" onClick={save} data-testid="growth-yt-save">Salva</Button>
      <span className="text-xs text-stone">{d.youtube_auto ? "YouTube letto in automatico" : "YouTube: serve l'ID canale e la chiave Google"}</span>
    </div>
  );
}

function Followers({ history, onSnap }) {
  const last = history[0] || {}; const prev = history[1] || {};
  const [m, setM] = useState({ tiktok: "", youtube: "" });
  const [sending, setSending] = useState(false);
  const snap = async () => {
    const body = Object.fromEntries(Object.entries(m).filter(([, v]) => v !== "").map(([k, v]) => [k, Number(v)]));
    try { await api.post("/growth/followers", body); toast.success("Follower aggiornati"); setM({ tiktok: "", youtube: "" }); onSnap(); } catch { toast.error("Errore"); }
  };
  return (
    <div className="rounded-2xl border border-line bg-canvas p-5 space-y-4" data-testid="growth-followers">
      <div className="font-serif text-lg text-ink">Follower per canale</div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {KEYS.map((k) => {
          const d = last[k] != null && prev[k] != null ? last[k] - prev[k] : null;
          return (
            <div key={k} className="rounded-xl bg-sand/50 p-3" data-testid={`growth-f-${k}`}>
              <div className="text-xs uppercase tracking-widest text-stone">{CH[k]}</div>
              <div className="font-serif text-3xl text-ink">{last[k] ?? "—"}</div>
              {d != null && <div className={`text-xs ${d >= 0 ? "text-olive" : "text-terracotta"}`}>{d >= 0 ? "+" : ""}{d} dall'ultima rilevazione</div>}
            </div>
          );
        })}
      </div>
      <div className="flex flex-wrap gap-2 items-center">
        {["tiktok", "youtube"].map((k) => <Input key={k} type="number" className="w-44" placeholder={`${CH[k]} (manuale)`} value={m[k]} onChange={(e) => setM({ ...m, [k]: e.target.value })} data-testid={`growth-input-${k}`} />)}
        <Button className="rounded-full bg-ink text-canvas" onClick={snap} data-testid="growth-snapshot-btn">Aggiorna numeri</Button>
        <Button variant="outline" className="rounded-full" disabled={sending} onClick={async () => { setSending(true); try { await api.post("/growth/report-now"); toast.success("Riepilogo inviato alla tua email"); onSnap(); } catch { toast.error("Errore"); } finally { setSending(false); } }} data-testid="growth-report-now-btn">{sending ? "Invio…" : "Inviami ora il riepilogo"}</Button>
      </div>
      <div className="text-xs text-stone">Ogni lunedì alle 9:00 Otelya rileva da solo Facebook, Instagram, YouTube e i lettori del blog, e ti manda un'email con risultati e consigli. TikTok va inserito a mano: TikTok non permette la lettura automatica senza la sua approvazione.</div>
    </div>
  );
}

export default function GrowthPlan() {
  const [d, setD] = useState(null);
  const load = () => api.get("/growth/plan").then((r) => setD(r.data));
  useEffect(() => { load(); }, []);
  if (!d) return null;
  const toggle = async (id) => { const r = await api.post(`/growth/task/${id}`); setD({ ...d, done: r.data.done }); };
  const pct = Math.round((d.done.length / d.tasks.length) * 100);
  const groups = Object.keys(CH).filter((c) => d.tasks.some((t) => t.channel === c));
  return (
    <div className="space-y-6" data-testid="growth-plan">
      <Followers history={d.history} onSnap={load} />
      <YouTubeId d={d} onSaved={load} />
      <div className="rounded-2xl border border-line bg-canvas p-5 space-y-4">
        <div className="flex justify-between items-center flex-wrap gap-2">
          <div><div className="font-serif text-lg text-ink">Azioni della settimana {d.week}</div><div className="text-xs text-stone">Quando Otelya pubblica Reel, storie, articoli e post, le azioni si spuntano da sole. Le altre spuntale tu.</div></div>
          <div className="text-sm text-stone" data-testid="growth-progress">{d.done.length}/{d.tasks.length} fatte · {pct}%</div>
        </div>
        <div className="h-2 rounded-full bg-sand overflow-hidden"><div className="h-full bg-olive transition-[width] duration-500" style={{ width: `${pct}%` }} /></div>
        {groups.map((g) => (
          <div key={g} className="space-y-1">
            <div className="text-xs uppercase tracking-widest text-stone mt-3">{g === "blog" ? "Blog" : CH[g]}</div>
            {d.tasks.filter((t) => t.channel === g).map((t) => {
              const on = d.done.includes(t.id);
              return (
                <button key={t.id} onClick={() => toggle(t.id)} data-testid={`growth-task-${t.id}`}
                  className="w-full flex items-center gap-3 text-left px-3 py-2 rounded-lg hover:bg-sand/60 transition-colors">
                  {on ? <CheckCircle size={20} weight="fill" className="text-olive shrink-0" /> : <Circle size={20} className="text-stone shrink-0" />}
                  <span className={`text-sm ${on ? "line-through text-stone" : "text-ink"}`}>{t.title}</span>
                  {d.auto.includes(t.id) && <span className="text-[10px] uppercase tracking-widest text-olive" data-testid={`growth-auto-${t.id}`}>automatica</span>}
                  <span className="ml-auto text-[10px] uppercase tracking-widest text-stone">{t.freq}</span>
                </button>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
